import React, { useState } from 'react';
import ContractModal from './ContractModal';
import SellerSelect from './SellerSelect';
import {
    registerPayment, suspendContract, reactivateContract, endContract, convertContract, changeContractSeller,
    errorMessage,
} from '../../services/contracts/contractsService';
import { isOpen, isoDatePlusDays, inputClass, labelClass, actionLink, dangerLink, formatDate } from './contractLabels';

// Fecha sugerida para un pago nuevo: un mes después de lo ya pagado (o desde hoy si ya venció)
const suggestedPaymentDate = (contract) => {
    const today = isoDatePlusDays(0);
    const base = contract.validUntil && contract.validUntil > today ? contract.validUntil : today;
    const [y, m, d] = base.split('-').map(Number);
    const next = new Date(y, m, d); // mes siguiente (los meses de Date empiezan en 0)
    const pad = (n) => String(n).padStart(2, '0');
    return `${next.getFullYear()}-${pad(next.getMonth() + 1)}-${pad(next.getDate())}`;
};

// Acciones del administrador sobre un contrato; onChanged recarga la lista
const ContractActions = ({ contract, sensors = [], onChanged }) => {
    const [modal, setModal] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [paymentDate, setPaymentDate] = useState('');
    const [convert, setConvert] = useState({});
    const [sellerEmail, setSellerEmail] = useState('');

    if (!isOpen(contract)) {
        return <span className="text-gray-400 text-sm">-</span>;
    }

    const close = () => { setModal(null); setError(null); };

    const run = async (action, successMessage) => {
        setBusy(true);
        setError(null);
        try {
            await action();
            close();
            if (successMessage) alert(successMessage);
            onChanged && onChanged();
        } catch (e) {
            setError(errorMessage(e));
            if (!modal) alert(errorMessage(e));
        } finally {
            setBusy(false);
        }
    };

    const confirmAndRun = (question, action, successMessage) => {
        if (window.confirm(question)) run(action, successMessage);
    };

    const openPayment = () => { setPaymentDate(suggestedPaymentDate(contract)); setModal('payment'); };
    const openConvert = () => {
        setConvert({ type: 'PURCHASE', validUntil: isoDatePlusDays(30), keepDevice: true, newSensorId: '', notes: '' });
        setModal('convert');
    };

    const openSeller = () => { setSellerEmail(contract.sellerEmail || ''); setModal('seller'); };

    const label = `${contract.sensorId}${contract.userName ? ' de ' + contract.userName : ''}`;
    const isTrial = contract.type === 'TRIAL';
    const isPaid = contract.type === 'RENT' || contract.type === 'PURCHASE';

    return (
        <div className="flex flex-wrap gap-x-3 gap-y-1">
            {isPaid && <button type="button" className={actionLink} onClick={openPayment}>Registrar pago</button>}
            {isTrial && <button type="button" className={actionLink} onClick={openConvert}>Convertir</button>}
            {(isTrial || isPaid) && <button type="button" className={actionLink} onClick={openSeller}>Vendedor</button>}
            {contract.status === 'ACTIVE' && (
                <button type="button" className={actionLink}
                    onClick={() => confirmAndRun(`¿Suspender el acceso al sensor ${label}?`,
                        () => suspendContract(contract.id), 'Contrato suspendido.')}>
                    Suspender
                </button>
            )}
            {contract.status === 'SUSPENDED' && (
                <button type="button" className={actionLink}
                    onClick={() => confirmAndRun(`¿Reactivar el acceso al sensor ${label}?`,
                        () => reactivateContract(contract.id), 'Contrato reactivado.')}>
                    Reactivar
                </button>
            )}
            <button type="button" className={dangerLink}
                onClick={() => confirmAndRun(
                    `¿Terminar el contrato del sensor ${label}? El usuario pierde el acceso a ese sensor.`,
                    () => endContract(contract.id), 'Contrato terminado.')}>
                Terminar
            </button>

            {modal === 'payment' && (
                <ContractModal title={`Registrar pago · ${label}`} onClose={close} busy={busy} error={error}
                    confirmLabel="Registrar pago"
                    onConfirm={() => run(() => registerPayment(contract.id, paymentDate), 'Pago registrado.')}>
                    <p className="text-sm text-gray-700">
                        Pagado actualmente hasta: <b>{formatDate(contract.validUntil)}</b>.
                        {contract.status === 'SUSPENDED' && ' Al registrar el pago se reactiva el acceso.'}
                    </p>
                    <div>
                        <label className={labelClass} htmlFor="paymentDate">Pagado hasta</label>
                        <input id="paymentDate" type="date" className={inputClass} required
                            value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} />
                    </div>
                </ContractModal>
            )}

            {modal === 'seller' && (
                <ContractModal title={`Vendedor · ${label}`} onClose={close} busy={busy} error={error}
                    confirmLabel="Guardar"
                    onConfirm={() => run(() => changeContractSeller(contract.id, sellerEmail), 'Vendedor actualizado.')}>
                    <div>
                        <label className={labelClass} htmlFor="contractSellerEmail">Vendedor</label>
                        <SellerSelect id="contractSellerEmail" value={sellerEmail} onChange={setSellerEmail} />
                        <p className="text-xs text-gray-600 mt-1">
                            {isTrial
                                ? 'El vendedor administra este préstamo y ve los datos del equipo.'
                                : 'El vendedor puede ver los datos de este equipo y ajustar sus umbrales para asesorar al cliente, mientras el contrato siga abierto.'}
                        </p>
                    </div>
                </ContractModal>
            )}

            {modal === 'convert' && (
                <ContractModal title={`Convertir préstamo · ${label}`} onClose={close} busy={busy} error={error}
                    confirmLabel="Convertir"
                    onConfirm={() => run(() => convertContract(contract.id, {
                        ...convert,
                        validUntil: convert.validUntil || null,
                        newSensorId: convert.keepDevice ? null : convert.newSensorId,
                    }), 'Préstamo convertido.')}>
                    <div>
                        <label className={labelClass} htmlFor="convertType">Pasa a</label>
                        <select id="convertType" className={inputClass} value={convert.type}
                            onChange={(e) => setConvert({ ...convert, type: e.target.value })}>
                            <option value="PURCHASE">Compra (+ plataforma mensual)</option>
                            <option value="RENT">Alquiler</option>
                        </select>
                        {convert.type === 'RENT' && (
                            <p className="text-xs text-gray-600 mt-1">
                                Si corresponde, cambia el rol del usuario a "Usuario (alquiler)" en su ficha.
                            </p>
                        )}
                    </div>
                    <div>
                        <label className={labelClass} htmlFor="convertUntil">Pagado hasta</label>
                        <input id="convertUntil" type="date" className={inputClass}
                            value={convert.validUntil} onChange={(e) => setConvert({ ...convert, validUntil: e.target.value })} />
                        <p className="text-xs text-gray-600 mt-1">Vacío = sin vencimiento.</p>
                    </div>
                    <div className="space-y-2">
                        <span className={labelClass}>Equipo</span>
                        <label className="flex items-center gap-2 text-sm">
                            <input type="radio" checked={convert.keepDevice}
                                onChange={() => setConvert({ ...convert, keepDevice: true })} />
                            Se queda con el equipo prestado ({contract.sensorId})
                        </label>
                        <label className="flex items-center gap-2 text-sm">
                            <input type="radio" checked={!convert.keepDevice}
                                onChange={() => setConvert({ ...convert, keepDevice: false })} />
                            Se le entrega otro equipo (el prestado vuelve al inventario)
                        </label>
                        {!convert.keepDevice && (
                            <select className={inputClass} value={convert.newSensorId} required
                                onChange={(e) => setConvert({ ...convert, newSensorId: e.target.value })}>
                                <option value="" disabled>Selecciona el equipo</option>
                                {sensors.filter((s) => s.id !== contract.sensorId).map((s) => (
                                    <option key={s.id} value={s.id}>{s.id}{s.name ? ` · ${s.name}` : ''}</option>
                                ))}
                            </select>
                        )}
                    </div>
                    <div>
                        <label className={labelClass} htmlFor="convertNotes">Notas</label>
                        <input id="convertNotes" type="text" className={inputClass} value={convert.notes}
                            onChange={(e) => setConvert({ ...convert, notes: e.target.value })} />
                    </div>
                </ContractModal>
            )}
        </div>
    );
};

export default ContractActions;
